import * as React from "react";
import { OptionType } from "@/services/type";

export enum Direction {
  up = "up",
  down = "down",
}

export interface KeyboardAccessibleListProps<ValueType, OptionType> {
  /**
   * if `false`, a list will be close and disable all interactivity
   */
  isEnable: boolean;

  /**
   * if `true`, it will display loading indicator on list and disable other interactivity
   */
  isLoading?: boolean;
  options: OptionType[];

  /**
   * list of selected options based on unique key, in other words `OptionType["value"]
   */
  selectedValues: ValueType[];

  /**
   * CSS class which is injected to `<ul/>` container
   */
  containerClasses?: string;

  /**
   * capture enter key and returns the option that the `isSelectingIndex` refers to
   * It works only if:
   * - isEnabled === true
   * - isLoading === false
   * - option.length > 0
   * @param option
   * @param index
   */
  onEnter?(option: OptionType, index: number): void;

  /**
   * capture mouse click on `<li/>` item and return corresponding option
   * It works only if:
   * - isEnabled === true
   * - isLoading === false
   * - option.length > 0
   * @param option
   * @param index
   */
  onClick?(option: OptionType, index: number): void;

  /**
   * capture escape key
   * It works only if:
   * - isEnabled === true
   */
  onEscape?(): void;

  /**
   * render content for inside of `<li/>`
   * @param option
   */
  renderItem?(option: OptionType): React.ReactNode;

  /**
   * if `true`, UI would assume as non-(focusable/clickable/navigable/hoverable) item
   * @param option
   */
  isActiveElement?(option: OptionType): boolean;
}

export function setScrollPosition(
  containerElement: HTMLUListElement | null,
  currentIndex: number
): void {
  if (!containerElement) return;

  const { children } = containerElement;
  const nthChildOfElement = children.item(currentIndex);

  if (nthChildOfElement) {
    nthChildOfElement.scrollIntoView({
      block: "nearest",
    });
  }
}

export function findNewIndex<ValueType, OT extends OptionType<ValueType>>(
  options: OT[],
  currentIndex: number,
  direction: Direction,
  isActiveElement: KeyboardAccessibleListProps<ValueType, OT>["isActiveElement"]
): number {
  if (options.length) {
    const maxIndex = options.length - 1;
    let newSelectedIndex = currentIndex;

    do {
      switch (direction) {
        case Direction.up:
          newSelectedIndex--;
          break;
        case Direction.down:
          newSelectedIndex++;
          break;
      }

      if (newSelectedIndex < 0 || newSelectedIndex > maxIndex) {
        return currentIndex;
      }
    } while (!isActiveElement?.(options[newSelectedIndex]));

    return newSelectedIndex;
  }

  return currentIndex;
}
